import React, { useState, useMemo } from 'react';
import MaterialIcon from '../ui/MaterialIcon';
import LOYALTY_PROGRAMS, { CATEGORIES, getCurrentTier } from '../../data/loyaltyPrograms';
import { useLoyalty } from '../../contexts/LoyaltyContext';

const CATEGORY_ICONS = {
  airline: 'flight',
  hotel: 'hotel',
  retail: 'shopping_bag',
};

function AddProgramModal({ onClose }) {
  const { enrollProgram, programs: enrolled } = useLoyalty();
  const enrolledIds = new Set(enrolled.map((p) => p.programId));

  const [step, setStep] = useState('search'); // 'search' | 'details'
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [query, setQuery] = useState('');

  // Details form
  const [balance, setBalance] = useState('');
  const [qualifyingUnits, setQualifyingUnits] = useState('');
  const [memberNumber, setMemberNumber] = useState('');

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return LOYALTY_PROGRAMS.filter((p) => {
      const matchesCategory = activeCategory === 'all' || p.category === activeCategory;
      const matchesQuery = !q || p.name.toLowerCase().includes(q);
      return matchesCategory && matchesQuery;
    });
  }, [query, activeCategory]);

  const handleSelectProgram = (program) => {
    setSelectedProgram(program);
    setBalance('');
    setQualifyingUnits('');
    setMemberNumber('');
    setStep('details');
  };

  const handleSave = () => {
    if (!balance) return;
    const qual = qualifyingUnits || balance;
    const tier = getCurrentTier(selectedProgram.id, Number(qual));
    enrollProgram(selectedProgram.id, {
      balance,
      qualifyingUnits: qual,
      tier,
      memberNumber,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      {/* Sheet */}
      <div className="relative w-full max-w-lg bg-surface rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col max-h-[88vh]">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-outline-variant/50" />
        </div>

        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-outline-variant/15">
          {step === 'details' && (
            <button
              onClick={() => setStep('search')}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-surface-container transition-colors shrink-0"
            >
              <MaterialIcon name="arrow_back" size={20} className="text-on-surface" />
            </button>
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-on-surface">
              {step === 'search' ? 'Add Loyalty Program' : selectedProgram?.name}
            </h2>
            {step === 'details' && (
              <p className="text-xs text-on-surface-variant capitalize">{selectedProgram?.category}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-surface-container transition-colors shrink-0"
          >
            <MaterialIcon name="close" size={20} className="text-on-surface-variant" />
          </button>
        </div>

        {/* ── Step 1: Search ── */}
        {step === 'search' && (
          <>
            {/* Search box */}
            <div className="px-5 pt-4">
              <div className="flex items-center gap-3 px-4 py-2.5 bg-surface-container rounded-2xl">
                <MaterialIcon name="search" size={20} className="text-on-surface-variant shrink-0" />
                <input
                  type="text"
                  placeholder="Search programs..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                  className="flex-1 bg-transparent text-sm text-on-surface placeholder-on-surface-variant outline-none"
                />
                {query && (
                  <button onClick={() => setQuery('')}>
                    <MaterialIcon name="close" size={16} className="text-on-surface-variant" />
                  </button>
                )}
              </div>
            </div>

            {/* Category chips */}
            <div className="flex gap-2 px-5 py-3 overflow-x-auto no-scrollbar">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    activeCategory === cat.id
                      ? 'bg-primary text-on-primary'
                      : 'bg-surface-container text-on-surface-variant'
                  }`}
                >
                  {cat.id !== 'all' && (
                    <MaterialIcon name={CATEGORY_ICONS[cat.id]} size={14} />
                  )}
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Program list */}
            <div className="overflow-y-auto flex-1 px-5 pb-6 space-y-2">
              {filtered.map((program) => {
                const isEnrolled = enrolledIds.has(program.id);
                return (
                  <button
                    key={program.id}
                    onClick={() => !isEnrolled && handleSelectProgram(program)}
                    disabled={isEnrolled}
                    className={`w-full flex items-center gap-4 p-4 rounded-2xl text-left transition-colors ${
                      isEnrolled
                        ? 'bg-surface-container/50 opacity-60 cursor-not-allowed'
                        : 'bg-surface-container hover:bg-surface-container-high active:bg-primary/5'
                    }`}
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${program.accentColor}18` }}
                    >
                      <MaterialIcon
                        name={program.icon}
                        size={22}
                        style={{ color: program.accentColor }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-on-surface">{program.name}</p>
                      <p className="text-xs text-on-surface-variant capitalize">{program.category}</p>
                    </div>
                    {isEnrolled ? (
                      <span className="text-xs font-medium text-success bg-success-light px-2.5 py-1 rounded-full">
                        Added
                      </span>
                    ) : (
                      <MaterialIcon name="chevron_right" size={20} className="text-on-surface-variant shrink-0" />
                    )}
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <div className="text-center py-10">
                  <MaterialIcon name="search_off" size={40} className="text-on-surface-variant/40" />
                  <p className="text-sm text-on-surface-variant mt-3">No programs found</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* ── Step 2: Details ── */}
        {step === 'details' && selectedProgram && (
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
            {/* Program badge */}
            <div
              className="flex items-center gap-4 p-4 rounded-2xl"
              style={{ backgroundColor: `${selectedProgram.accentColor}12` }}
            >
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: `${selectedProgram.accentColor}20` }}
              >
                <MaterialIcon name={selectedProgram.icon} size={26} style={{ color: selectedProgram.accentColor }} />
              </div>
              <div>
                <p className="text-base font-bold text-on-surface">{selectedProgram.name}</p>
                <p className="text-xs text-on-surface-variant capitalize">{selectedProgram.category} program</p>
              </div>
            </div>

            {/* Balance */}
            <div>
              <label className="block text-sm font-semibold text-on-surface mb-2">
                Current Balance <span className="text-error">*</span>
              </label>
              <div className="flex items-center gap-3 px-4 py-3 bg-surface-container rounded-2xl">
                <input
                  type="number"
                  placeholder="0"
                  value={balance}
                  onChange={(e) => setBalance(e.target.value)}
                  className="flex-1 bg-transparent text-base font-semibold text-on-surface outline-none"
                  min="0"
                />
                <span className="text-sm text-on-surface-variant font-medium shrink-0">
                  {selectedProgram.unit}
                </span>
              </div>
            </div>

            {/* Qualifying units (for tier calculation) */}
            {selectedProgram.tiers.length > 1 && (
              <div>
                <label className="block text-sm font-semibold text-on-surface mb-1">
                  Qualifying {selectedProgram.category === 'hotel' ? 'Nights' : selectedProgram.unit} (this year)
                </label>
                <p className="text-xs text-on-surface-variant mb-2">Used to determine your tier status</p>
                <div className="flex items-center gap-3 px-4 py-3 bg-surface-container rounded-2xl">
                  <input
                    type="number"
                    placeholder="Same as balance"
                    value={qualifyingUnits}
                    onChange={(e) => setQualifyingUnits(e.target.value)}
                    className="flex-1 bg-transparent text-base text-on-surface outline-none"
                    min="0"
                  />
                  <span className="text-sm text-on-surface-variant font-medium shrink-0">
                    {selectedProgram.category === 'hotel' ? 'nights' : selectedProgram.unit}
                  </span>
                </div>
              </div>
            )}

            {/* Member number */}
            <div>
              <label className="block text-sm font-semibold text-on-surface mb-2">
                Member Number <span className="text-on-surface-variant font-normal">(optional)</span>
              </label>
              <div className="px-4 py-3 bg-surface-container rounded-2xl">
                <input
                  type="text"
                  placeholder="e.g. UA123456789"
                  value={memberNumber}
                  onChange={(e) => setMemberNumber(e.target.value)}
                  className="w-full bg-transparent text-base text-on-surface outline-none"
                />
              </div>
            </div>

            {/* Tier preview */}
            {balance && selectedProgram.tiers.length > 1 && (
              <div className="flex items-center gap-3 px-4 py-3.5 bg-primary/5 rounded-2xl border border-primary/10">
                <MaterialIcon name="workspace_premium" size={22} className="text-primary shrink-0" />
                <div>
                  <p className="text-xs text-on-surface-variant">Estimated tier</p>
                  <p className="text-sm font-semibold text-on-surface">
                    {getCurrentTier(selectedProgram.id, Number(qualifyingUnits || balance))}
                  </p>
                </div>
              </div>
            )}

            {/* Save button */}
            <button
              onClick={handleSave}
              disabled={!balance}
              className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-primary via-primary to-primary-dark text-on-primary font-semibold text-sm active:scale-[0.97] duration-200 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              Add Program
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AddProgramModal;
